/**
 * Merchant Pro V1 PR-4a — Live Floor Plan page.
 *
 * Standalone /owner/floor-plan route. Operator picks a branch, then sees
 * room tabs + chair-grid cards driven by GET /api/owner/branches/{branch_id}/floor-plan/state
 * (V4-A2). Polling cadence: 30s.
 *
 * Tap a card → opens the TableActionSheet for state mutations
 * (block / unblock / mark cleaning / mark playing / mark available).
 *
 * PR-Audit-02B G-02 + G-03 (2026-06-19) — Room + Table CRUD UI:
 *   * "+ Add Room" button (brand_owner + super_admin only) opens
 *     RoomEditorModal for the active branch.
 *   * Active room tab gets inline Edit + Delete icon buttons.
 *   * "+ Add Table" button (visible only when a room is active) opens
 *     TableEditorModal scoped to the active room.
 *   * Manage-tables list below the canvas exposes per-table Edit
 *     + Delete affordances for the active room. Reuses chair-grid
 *     canvas read path — no fork, no duplicate engine.
 *   * Cashier role sees the canvas but NOT the CRUD affordances
 *     (backend `require_pro_brand_path` denies anyway; UI hides
 *     for clarity).
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Plus, Pencil, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import { FloorPlanLegend } from '@/components/owner/ChairGridTableCard';
import WebFloorPlanCanvas from '@/components/owner/WebFloorPlanCanvas';
import TableActionSheet from '@/components/owner/TableActionSheet';
import RoomEditorModal from '@/components/owner/RoomEditorModal';
import TableEditorModal from '@/components/owner/TableEditorModal';

export default function LiveFloorPlanPage() {
  const { token, user } = useAuthStore();
  const queryClient = useQueryClient();
  const brandId = user?.brand_id;
  // G-02 + G-03 — Room/Table CRUD gated by `brand_owner` or
  // `super_admin` to mirror backend `require_pro_brand_path` policy.
  const canManageFloor = user?.role === 'brand_owner' || user?.role === 'super_admin';

  // Branch selector — defaults to first branch the actor sees.
  const { data: branches = [] } = useQuery({
    queryKey: qk.brandBranches(brandId || 'self'),
    queryFn: () => api.brandBranches(token, brandId),
    enabled: !!token && !!brandId,
    staleTime: 60_000,
  });

  const [branchId, setBranchId] = useState(null);
  useEffect(() => {
    if (!branchId && branches.length > 0) {
      setBranchId(branches[0].id);
    }
  }, [branches, branchId]);

  // Live state — polls every 30s (operator C-4).
  const {
    data: state,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: qk.ownerFloorPlanState(branchId),
    queryFn: () => api.ownerFloorPlanState(token, branchId),
    enabled: !!token && !!branchId,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const rooms = useMemo(() => state?.rooms ?? [], [state]);
  const tables = useMemo(() => state?.tables ?? [], [state]);
  const tablesByRoom = useMemo(() => {
    const grouped = {};
    for (const t of tables) {
      (grouped[t.room_id] = grouped[t.room_id] || []).push(t);
    }
    return grouped;
  }, [tables]);

  const [activeRoomId, setActiveRoomId] = useState(null);
  useEffect(() => {
    if (!activeRoomId && rooms.length > 0) {
      setActiveRoomId(rooms[0].id);
    }
  }, [rooms, activeRoomId]);

  // Action sheet state — selected table.
  const [selectedTable, setSelectedTable] = useState(null);
  const onCardClick = (table) => setSelectedTable(table);
  const closeSheet = () => setSelectedTable(null);
  const onActionApplied = async () => {
    closeSheet();
    await queryClient.invalidateQueries({ queryKey: qk.ownerFloorPlanState(branchId) });
  };

  // G-02 — Room editor modal state.
  const [roomModalMode, setRoomModalMode] = useState(null);   // 'create' | 'edit' | 'delete' | null
  const [roomTarget, setRoomTarget] = useState(null);

  // G-03 — Table editor modal state.
  const [tableModalMode, setTableModalMode] = useState(null);
  const [tableTarget, setTableTarget] = useState(null);

  const refresh = async () => { await refetch(); };
  const activeRoom = rooms.find((r) => r.id === activeRoomId);
  const activeRoomTables = activeRoomId ? (tablesByRoom[activeRoomId] || []) : [];

  return (
    <div className="space-y-6" data-testid="floor-plan-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-gold/85 font-semibold">
            Merchant Pro V1 · PR-4a
          </div>
          <h1 className="text-2xl font-semibold mt-1" data-testid="floor-plan-page-title">
            Live Floor Plan
          </h1>
          <p className="text-sm text-white/55 mt-1">
            Operator-driven table state across every room. Updates every 30 seconds.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={branchId ?? ''}
            onChange={(e) => setBranchId(e.target.value || null)}
            data-testid="floor-plan-branch-selector"
            className="px-3 py-2 rounded-2xl bg-[#15151B] border border-white/10 text-sm text-white/90 focus:outline-none focus:ring-2 focus:ring-gold/60 min-h-[44px]"
          >
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{b.name} {b.city ? `· ${b.city}` : ''}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={refresh}
            disabled={isFetching}
            data-testid="floor-plan-refresh"
            className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-[#15151B] border border-white/10 text-sm text-white/80 hover:bg-white/5 disabled:opacity-60 min-h-[44px]"
          >
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
            Refresh
          </button>
          {canManageFloor && branchId ? (
            <button
              type="button"
              onClick={() => { setRoomTarget(null); setRoomModalMode('create'); }}
              data-testid="floor-plan-add-room"
              className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-gold/15 border border-gold/40 text-sm text-gold hover:bg-gold/25 min-h-[44px]"
            >
              <Plus size={14} /> Add room
            </button>
          ) : null}
        </div>
      </div>

      {/* Room tabs */}
      {rooms.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2" data-testid="floor-plan-room-tabs">
          {rooms.map((r) => {
            const active = r.id === activeRoomId;
            const count = (tablesByRoom[r.id] || []).length;
            return (
              <div key={r.id} className="flex items-center gap-1">
                <button
                  onClick={() => setActiveRoomId(r.id)}
                  data-testid={`floor-plan-room-tab-${r.id}`}
                  data-active={active ? 'true' : 'false'}
                  className={
                    'px-4 py-2 rounded-2xl text-sm font-medium transition-colors border min-h-[44px] ' +
                    (active
                      ? 'bg-gold/15 text-gold border-gold/40'
                      : 'bg-[#15151B] text-white/70 border-white/10 hover:bg-white/5')
                  }
                >
                  <span>{r.name}</span>
                  <span className="ml-2 text-xs text-white/40">{count}</span>
                </button>
                {canManageFloor && active ? (
                  <>
                    <button
                      type="button"
                      onClick={() => { setRoomTarget(r); setRoomModalMode('edit'); }}
                      data-testid={`floor-plan-room-edit-${r.id}`}
                      className="inline-flex items-center justify-center w-11 h-11 rounded-2xl text-white/70 hover:bg-white/5 hover:text-white border border-white/10"
                      aria-label={`Edit room ${r.name}`}
                      title="Edit room"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => { setRoomTarget(r); setRoomModalMode('delete'); }}
                      data-testid={`floor-plan-room-delete-${r.id}`}
                      className="inline-flex items-center justify-center w-11 h-11 rounded-2xl text-red-300/80 hover:bg-red-600/10 hover:text-red-300 border border-red-700/30"
                      aria-label={`Delete room ${r.name}`}
                      title="Delete room"
                    >
                      <Trash2 size={14} />
                    </button>
                  </>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {/* Grid */}
      {isLoading ? (
        <div className="text-white/50 text-sm py-12 text-center" data-testid="floor-plan-loading">
          Loading floor plan…
        </div>
      ) : rooms.length === 0 ? (
        <div className="text-white/40 text-sm py-12 text-center" data-testid="floor-plan-empty">
          No rooms configured for this branch.
          {canManageFloor ? (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => { setRoomTarget(null); setRoomModalMode('create'); }}
                data-testid="floor-plan-empty-add-room"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-gold/15 border border-gold/40 text-sm text-gold hover:bg-gold/25 min-h-[44px]"
              >
                <Plus size={14} /> Create first room
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        <WebFloorPlanCanvas
          tables={activeRoomTables}
          room={activeRoom}
          selectedTableId={selectedTable?.id}
          onTableClick={onCardClick}
          testIdPrefix="floor-plan"
          minCardWidth={180}
          emptyMessage="This room has no tables yet."
        />
      )}

      {/* Legend */}
      {rooms.length > 0 ? <FloorPlanLegend /> : null}

      {/* G-03 — Manage Tables strip — visible only to brand_owner/super_admin */}
      {canManageFloor && activeRoom ? (
        <div className="rounded-2xl border border-white/10 bg-[#15151B] p-4 space-y-3" data-testid="floor-plan-table-manager">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <h3 className="text-sm font-semibold text-white">Manage tables in {activeRoom.name}</h3>
              <p className="text-xs text-white/55 mt-0.5">Edit or remove tables. State changes (block / cleaning / playing) remain on the canvas action sheet.</p>
            </div>
            <button
              type="button"
              onClick={() => { setTableTarget(null); setTableModalMode('create'); }}
              data-testid="floor-plan-add-table"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl bg-gold/15 border border-gold/40 text-sm text-gold hover:bg-gold/25 min-h-[44px]"
            >
              <Plus size={14} /> Add table
            </button>
          </div>
          {activeRoomTables.length === 0 ? (
            <div className="text-xs text-white/40 italic py-2">This room has no tables yet. Create the first one to start placing reservations.</div>
          ) : (
            <ul className="divide-y divide-white/5" data-testid="floor-plan-table-list">
              {activeRoomTables.map((t) => (
                <li
                  key={t.id}
                  className="flex items-center justify-between gap-3 py-2"
                  data-testid={`floor-plan-table-row-${t.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-white truncate">{t.name}</div>
                    <div className="text-xs text-white/45">Capacity: {t.capacity} · State: {t.state}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => { setTableTarget(t); setTableModalMode('edit'); }}
                      data-testid={`floor-plan-table-edit-${t.id}`}
                      className="inline-flex items-center justify-center w-11 h-11 rounded-2xl text-white/70 hover:bg-white/5 hover:text-white border border-white/10"
                      aria-label={`Edit table ${t.name}`}
                      title="Edit table"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => { setTableTarget(t); setTableModalMode('delete'); }}
                      data-testid={`floor-plan-table-delete-${t.id}`}
                      className="inline-flex items-center justify-center w-11 h-11 rounded-2xl text-red-300/80 hover:bg-red-600/10 hover:text-red-300 border border-red-700/30"
                      aria-label={`Delete table ${t.name}`}
                      title="Delete table"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {/* Action sheet — state mutations only (unchanged) */}
      <TableActionSheet
        open={!!selectedTable}
        table={selectedTable}
        roomName={activeRoom?.name}
        onClose={closeSheet}
        onApplied={onActionApplied}
      />

      {/* Room editor modal — G-02 */}
      <RoomEditorModal
        open={!!roomModalMode}
        mode={roomModalMode || 'create'}
        branchId={branchId}
        room={roomTarget}
        onClose={() => { setRoomModalMode(null); setRoomTarget(null); }}
        onSuccess={() => { /* invalidations happen inside the modal */ }}
      />

      {/* Table editor modal — G-03 */}
      <TableEditorModal
        open={!!tableModalMode}
        mode={tableModalMode || 'create'}
        branchId={branchId}
        roomId={activeRoomId}
        table={tableTarget}
        onClose={() => { setTableModalMode(null); setTableTarget(null); }}
        onSuccess={() => { /* invalidations happen inside the modal */ }}
      />
    </div>
  );
}
